import { apiUserInfo } from "@/api/acendex/user";
import { refreshApi, useFetch } from "@/lib/fetch-tools";
import { styled } from "@/styles/stitches.config";
import { domainSignal } from "@/utils/domain-store";
import { getText } from "@/utils/util";
import { ChevronDown, Download, LogOut, User } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const AccountButton = styled("button", {
  padding: "6px 16px",
  border: "none",
  borderRadius: "$1",
  backgroundColor: "#9750DD",
  color: "white",
  fontSize: "$2",
  cursor: "pointer",
  transition: "all 0.2s",
  display: "flex",
  alignItems: "center",
  gap: "4px",
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: "",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255,255,255,0.1)",
    transform: "scale(0)",
    opacity: 0,
    transition: "transform 0.3s, opacity 0.3s",
    borderRadius: "inherit",
  },

  "&:hover": {
    backgroundColor: "#8445C4",
    boxShadow: "0 4px 8px rgba(151, 80, 221, 0.25)",
    transform: "translateY(-1px)",
  },

  "&:hover::before": {
    opacity: 1,
    transform: "scale(1)",
  },

  "&:active": {
    transform: "scale(0.98) translateY(0)",
    boxShadow: "0 2px 4px rgba(151, 80, 221, 0.2)",
  },

  variants: {
    isOpen: {
      true: {
        backgroundColor: "#8445C4",
        boxShadow: "0 4px 8px rgba(151, 80, 221, 0.25)",
      },
    },
    theme: {
      light: {
        backgroundColor: "#9750DD",
        color: "white",
        "&:hover": {
          backgroundColor: "#8445C4",
        },
      },
      dark: {
        backgroundColor: "#6D28D9",
        color: "white",
        "&:hover": {
          backgroundColor: "#5B21B6",
        },
      },
    },
  },

  defaultVariants: {
    theme: "light",
  },
});

const ButtonIcon = styled("span", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.2s",

  variants: {
    rotate: {
      true: {
        transform: "rotate(180deg)",
      },
    },
  },
});

const DropdownContainer = styled("div", {
  position: "relative",
});

const Overlay = styled("div", {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0)",
  zIndex: 999,
});

type MenuState = "entering" | "entered" | "exiting" | "exited";

const DropdownMenu = styled("div", {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  width: "220px",
  backgroundColor: "white",
  borderRadius: "$1",
  boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
  padding: "$2",
  zIndex: 1000,
  transition: "opacity 0.25s, transform 0.25s",

  variants: {
    state: {
      entering: {
        opacity: 1,
        transform: "translateY(0)",
      },
      entered: {
        opacity: 1,
        transform: "translateY(0)",
      },
      exiting: {
        opacity: 0,
        transform: "translateY(-10px)",
      },
      exited: {
        opacity: 0,
        transform: "translateY(-10px)",
        pointerEvents: "none",
      },
    },
    theme: {
      light: {
        backgroundColor: "white",
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
      },
      dark: {
        backgroundColor: "#1E1B3B",
        boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
      },
    },
  },

  defaultVariants: {
    theme: "light",
  },
});

const UserInfo = styled("div", {
  padding: "$2",
  borderBottom: "1px solid rgba(0,0,0,0.1)",
  marginBottom: "$2",
  transition: "all 0.2s",
  borderRadius: "$1",

  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.02)",
  },

  variants: {
    theme: {
      light: {
        borderBottom: "1px solid rgba(0,0,0,0.1)",
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.02)",
        },
      },
      dark: {
        borderBottom: "1px solid rgba(255,255,255,0.1)",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.05)",
        },
      },
    },
  },

  defaultVariants: {
    theme: "light",
  },
});

const UserName = styled("div", {
  fontSize: "$2",
  fontWeight: 500,
  color: "$textLight",
  marginBottom: "4px",

  variants: {
    theme: {
      light: {
        color: "#334155",
      },
      dark: {
        color: "#E2E8F0",
      },
    },
  },

  defaultVariants: {
    theme: "light",
  },
});

const UserEmail = styled("div", {
  fontSize: "$1",
  color: "$textLight",

  variants: {
    theme: {
      light: {
        color: "#64748B",
      },
      dark: {
        color: "#94A3B8",
      },
    },
  },

  defaultVariants: {
    theme: "light",
  },
});

const MenuItem = styled("button", {
  width: "100%",
  padding: "$2",
  border: "none",
  borderRadius: "$1",
  backgroundColor: "transparent",
  color: "$textLight",
  fontSize: "$2",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  transition: "all 0.2s",
  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: "",
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "radial-gradient(circle, rgba(151, 80, 221, 0.15) 0%, rgba(151, 80, 221, 0) 70%)",
    opacity: 0,
    transform: "scale(0)",
    transformOrigin: "center",
    transition: "opacity 0.5s, transform 0.5s",
  },

  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.05)",
    color: "#9750DD",
    paddingLeft: "12px",
  },

  "&:active::after": {
    opacity: 1,
    transform: "scale(2)",
  },

  variants: {
    theme: {
      light: {
        color: "#334155",
        "&:hover": {
          backgroundColor: "rgba(0,0,0,0.05)",
          color: "#9750DD",
        },
      },
      dark: {
        color: "#E2E8F0",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.05)",
          color: "#A78BFA",
        },
      },
    },
  },

  defaultVariants: {
    theme: "light",
  },
});

const DownloadButton = styled("a", {
  width: "100%",
  padding: "$2",
  border: "none",
  borderRadius: "$1",
  backgroundColor: "transparent",
  color: "$textLight",
  fontSize: "$2",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  transition: "all 0.3s",
  position: "relative",
  overflow: "hidden",
  textDecoration: "none",
  marginBottom: "8px",

  "&::before": {
    content: "",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(151, 80, 221, 0.08)",
    transform: "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.3s ease",
    borderRadius: "inherit",
  },

  "&:hover": {
    backgroundColor: "rgba(151, 80, 221, 0.05)",
    color: "#9750DD",
    paddingLeft: "12px",
  },

  "&:hover::before": {
    transform: "scaleX(1)",
  },

  "&:active": {
    transform: "scale(0.98)",
  },

  variants: {
    theme: {
      light: {
        color: "#334155",
        "&:hover": {
          backgroundColor: "rgba(151, 80, 221, 0.05)",
          color: "#9750DD",
        },
      },
      dark: {
        color: "#E2E8F0",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.05)",
          color: "#A78BFA",
        },
      },
    },
  },

  defaultVariants: {
    theme: "light",
  },
});

const DownloadIcon = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.2s",

  variants: {
    theme: {
      light: {},
      dark: {},
    },
  },
});

export function UserAccount(props: { theme?: "light" | "dark" }) {
  const { theme = "light" } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [menuState, setMenuState] = useState<MenuState>("exited");
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { data } = useFetch(apiUserInfo);
  const userInfo = data?.data ?? null;

  // 处理点击外部关闭下拉菜单
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 处理菜单状态变化
  useEffect(() => {
    if (isOpen) {
      setMenuState("entering");
      const timer = setTimeout(() => setMenuState("entered"), 50);
      return () => clearTimeout(timer);
    } else {
      if (menuState !== "exited") {
        setMenuState("exiting");
        const timer = setTimeout(() => setMenuState("exited"), 250);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, menuState]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleLogout = () => {
    localStorage.removeItem("uid");
    localStorage.removeItem("username");
    localStorage.removeItem("x-access-token");
    localStorage.removeItem("userCode");
    refreshApi(apiUserInfo);
    navigate("/");
    handleClose();
  };

  const handleDownload = () => {
    navigate("/download");
    handleClose();
  };

  if (!userInfo?.id) {
    return (
      <AccountButton theme={theme} onClick={handleLogin}>
        {translate(getText("登錄"))}
      </AccountButton>
    );
  }

  return (
    <DropdownContainer ref={containerRef}>
      <AccountButton theme={theme} onClick={handleToggle} isOpen={isOpen}>
        <User size={16} />
        <span>{userInfo.username || translate(getText("账号"))}</span>
        <ButtonIcon rotate={isOpen}>
          <ChevronDown size={16} />
        </ButtonIcon>
      </AccountButton>
      {menuState !== "exited" && <Overlay onClick={handleClose} />}
      <DropdownMenu state={menuState} theme={theme}>
        <UserInfo theme={theme}>
          <UserName theme={theme}>{userInfo.username}</UserName>
          <UserEmail theme={theme}>
            {userInfo.phone || "user@example.com"}
          </UserEmail>
        </UserInfo>

        {domainSignal.value && domainSignal.value?.appStatus === 1 && (
          <DownloadButton
            href="/download"
            theme={theme}
            onClick={handleDownload}
          >
            <DownloadIcon theme={theme}>
              <Download size={16} />
            </DownloadIcon>
            APP {translate("下載")}
          </DownloadButton>
        )}

        <MenuItem theme={theme} onClick={handleLogout}>
          <LogOut size={16} />
          {translate(getText("退出登錄"))}
        </MenuItem>
      </DropdownMenu>
    </DropdownContainer>
  );
}
